import { type EventId } from '@solinkcorp/api-types';
import { useUserSession } from '@solinkcorp/authentication';
import { toast } from '@solinkcorp/solink-components';
import { EVENT_TYPE_BOOKMARK } from '@solinkcorp/vd-common';
import { type IEvent } from 'clients/PlatformApi';
import { type NewActivityType } from 'components/ScheduledActivityForm/useScheduledEventForm';
import moment from 'moment';
import React from 'react';
import useFluxStore from 'src/hooks/useFluxStore';
import useLocale from 'src/hooks/useLocale';
import usePlatformClient from 'src/hooks/usePlatformClient';
import { LocationStore } from 'stores/LocationStore';
import useSWR from 'swr';
import { uuidv4 } from 'utils/AlarmContactUtils';
import isShallowEqual from 'utils/isShallowEqual';
import {
  DEFAULT_TIMEZONE,
  EXCLUDE_FROM_COUNT_TAG,
  HIDE_FROM_DISPLAY_TAG,
  SCHEDULED_AI_CHAT_REASON,
} from 'vd-constants';

const MAX_EVENTS_QUERY_RETRY = 3;

export type UseSpotCheckPreview = ReturnType<typeof useSpotCheckPreview>;

export default function useSpotCheckPreview() {
  const locale = useLocale();
  const user = useUserSession();
  const camerasById = useFluxStore(
    LocationStore,
    (store) => store.getAllLocationCamerasDict(),
    isShallowEqual,
  );
  const PlatformApi = usePlatformClient();

  const [eventPreviewId, setEventPreviewId] = React.useState<string | null>(null);

  const [eventPollingState, setEventPollingState] = React.useState<
    'POLLING' | 'SUCCESSFUL' | 'PREVIEW_ERROR' | null
  >(null);

  const spotCheckEventPreview = useSWR(
    eventPreviewId ? ['spot-check-event-preview', eventPreviewId] : null,
    ([, eventId]) => {
      return PlatformApi.getEvent(eventId);
    },
    {
      refreshInterval: eventPollingState === 'POLLING' ? 5000 : 0,
      revalidateOnFocus: false,
      revalidateIfStale: false,
      errorRetryCount: MAX_EVENTS_QUERY_RETRY,
      onSuccess: (event: IEvent) => {
        if (!event?.videoLinkItems) {
          return;
        }

        const linkItemsHaveError =
          event?.videoLinkItems && event?.videoLinkItems?.some((item) => item?.metadata?.isError);

        if (linkItemsHaveError) {
          setEventPollingState('PREVIEW_ERROR');
        } else if ((event?.videoLinkItems?.length ?? 0) > 1) {
          setEventPollingState('SUCCESSFUL');
        }

        return event;
      },
    },
  );

  const createSpotCheckPreview = React.useCallback(
    async (activity: NewActivityType, cameraIds: string[]) => {
      const firstCamera = camerasById?.[cameraIds[0]];
      const location = firstCamera?.location;

      const title = `${activity.title} - ${locale.scheduledAIChat.spotCheck} ${locale.general.preview}`;

      const id = uuidv4() as EventId;

      const payload: Partial<IEvent> = {
        id,
        type: EVENT_TYPE_BOOKMARK,
        subtype: SCHEDULED_AI_CHAT_REASON,
        startTimeLocal: moment()
          .tz(location?.timezone ?? DEFAULT_TIMEZONE)
          .toDate(),
        endTimeLocal: moment()
          .tz(location?.timezone ?? DEFAULT_TIMEZONE)
          .toDate(),
        title,
        details: {
          scheduledActivity: {
            title,
            reason: SCHEDULED_AI_CHAT_REASON,
            metadata: {
              aiChatPrompt: activity.metadata?.aiChatPrompt,
              aiChatAnswerFormat: activity.metadata?.aiChatAnswerFormat ?? undefined,
              aiChatAnswerStates: activity.metadata?.aiChatAnswerStates,
              cameras: cameraIds.map((cameraId: any) => ({ chId: cameraId })),
            },
            schedule: activity.rule,
            reoccurringCount: 1,
          },
          generatedByEventStandard: true,
        },
        tags: [EXCLUDE_FROM_COUNT_TAG, HIDE_FROM_DISPLAY_TAG],
        isAlarm: false,
        startTime: new Date(),
        endTime: new Date(),
        locationId: firstCamera.deviceId,
        siteId: location.id,
        subtitle: `${locale.scheduledAIChat.spotCheck} - ${locale.general.preview}`,
        customerId: user.customerId,
        creator: user.userId,
      };

      try {
        setEventPollingState('POLLING');
        await PlatformApi.createEventIngest(payload, true);
        setEventPreviewId(id);
      } catch {
        toast.error(locale.general.unexpectedError);
        setEventPollingState(null);
        return;
      }
    },
    [user, PlatformApi, locale, camerasById],
  );

  return React.useMemo(
    () => ({
      ...spotCheckEventPreview,
      createSpotCheckPreview,
      eventPollingState,
      setEventPreviewId,
      setEventPollingState,
    }),
    [spotCheckEventPreview, createSpotCheckPreview, eventPollingState],
  );
}
